import { useEffect, useMemo, useState } from "react";
import { api, formatApiErrorDetail } from "@/lib/api";
import { Card, CardContent } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import {
    AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent,
    AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle,
} from "@/components/ui/alert-dialog";
import { RefreshCw, RotateCcw } from "lucide-react";
import { toast } from "sonner";
import { formatWIB } from "@/lib/datetime";
import StickyHeader from "@/components/StickyHeader";

export default function PesertaTerhapus() {
    const [items, setItems] = useState([]);
    const [prodiList, setProdiList] = useState([]);
    const [loading, setLoading] = useState(false);
    const [confirmRestore, setConfirmRestore] = useState(null);
    const [restoring, setRestoring] = useState(false);

    const prodiMap = useMemo(() => {
        const m = {};
        prodiList.forEach((p) => (m[p.id] = p));
        return m;
    }, [prodiList]);

    const load = async () => {
        setLoading(true);
        try {
            const { data } = await api.get("/peserta/terhapus");
            setItems(data);
        } catch {
            toast.error("Gagal memuat daftar peserta terhapus");
        } finally {
            setLoading(false);
        }
    };

    useEffect(() => {
        api.get("/prodi").then(({ data }) => setProdiList(data)).catch(() => {});
        load();
    }, []);

    const doRestore = async () => {
        if (!confirmRestore) return;
        setRestoring(true);
        try {
            await api.post(`/peserta/${confirmRestore.id}/pulihkan`);
            toast.success(`${confirmRestore.nama} berhasil dipulihkan`);
            setConfirmRestore(null);
            load();
        } catch (e) {
            toast.error(formatApiErrorDetail(e.response?.data?.detail));
        } finally {
            setRestoring(false);
        }
    };

    return (
        <div className="max-w-6xl mx-auto px-6 lg:px-8 py-8 space-y-4">
            <StickyHeader>
            <div className="flex flex-col md:flex-row md:items-end md:justify-between gap-3">
                <div>
                    <div className="text-[11px] uppercase tracking-widest text-slate-500 font-semibold">Peserta Didik</div>
                    <h1 className="text-3xl font-extrabold tracking-tight text-slate-900" style={{ fontFamily: "Plus Jakarta Sans" }}>
                        Peserta Terhapus
                    </h1>
                    <p className="text-sm text-slate-500 mt-1">
                        Data yang dihapus tetap tersimpan (tidak hilang permanen) dan bisa dipulihkan kapan saja.
                    </p>
                </div>
                <Button variant="outline" onClick={load}>
                    <RefreshCw className="w-4 h-4 mr-2" /> Muat Ulang
                </Button>
            </div>
            </StickyHeader>

            <Card className="border-slate-200 shadow-sm overflow-hidden">
                <div className="overflow-x-auto">
                    <table className="w-full compact">
                        <thead className="bg-slate-100">
                            <tr className="text-left text-xs uppercase tracking-wider text-slate-600 font-semibold">
                                <th>Nama</th>
                                <th>NPM</th>
                                <th>Prodi</th>
                                <th>Dihapus Oleh</th>
                                <th>Dihapus Pada</th>
                                <th className="text-right pr-4">Aksi</th>
                            </tr>
                        </thead>
                        <tbody>
                            {loading ? (
                                <tr><td colSpan={6} className="text-center py-10 text-slate-400">Memuat...</td></tr>
                            ) : items.length === 0 ? (
                                <tr><td colSpan={6} className="text-center py-10 text-slate-400">Tidak ada peserta yang terhapus.</td></tr>
                            ) : items.map((r) => (
                                <tr key={r.id} className="border-t border-slate-200 hover:bg-slate-50">
                                    <td className="font-semibold text-slate-800">{r.nama}</td>
                                    <td className="font-mono text-xs">{r.npm}</td>
                                    <td className="text-slate-600">{prodiMap[r.prodi_id]?.nama_prodi || "-"}</td>
                                    <td className="font-mono text-xs">{r.deleted_by || "-"}</td>
                                    <td className="text-xs text-slate-500 whitespace-nowrap">{formatWIB(r.deleted_at)}</td>
                                    <td className="text-right pr-2">
                                        <Button size="sm" variant="outline" onClick={() => setConfirmRestore(r)} data-testid={`restore-${r.id}`}>
                                            <RotateCcw className="w-3.5 h-3.5 mr-1.5" /> Pulihkan
                                        </Button>
                                    </td>
                                </tr>
                            ))}
                        </tbody>
                    </table>
                </div>
                <div className="px-4 py-2 border-t bg-slate-50 text-xs text-slate-500">
                    Total: <span className="font-bold text-slate-800">{items.length}</span> peserta terhapus
                </div>
            </Card>

            <AlertDialog open={!!confirmRestore} onOpenChange={(o) => !restoring && !o && setConfirmRestore(null)}>
                <AlertDialogContent>
                    <AlertDialogHeader>
                        <AlertDialogTitle>Pulihkan {confirmRestore?.nama}?</AlertDialogTitle>
                        <AlertDialogDescription>
                            Peserta ini akan muncul kembali di daftar Peserta Didik, pencarian, dashboard, dan export seperti biasa.
                            Data terkait (riwayat stase, rotasi klinik, orientasi RS) tidak pernah terhapus, jadi otomatis relevan lagi.
                        </AlertDialogDescription>
                    </AlertDialogHeader>
                    <AlertDialogFooter>
                        <AlertDialogCancel disabled={restoring}>Batal</AlertDialogCancel>
                        <AlertDialogAction disabled={restoring} className="bg-emerald-600 hover:bg-emerald-700" onClick={doRestore}>
                            {restoring ? "Memproses..." : "Ya, Pulihkan"}
                        </AlertDialogAction>
                    </AlertDialogFooter>
                </AlertDialogContent>
            </AlertDialog>
        </div>
    );
}
